// src/components/proposal-formats/FormsAndFormatsModePanel.tsx
//
// Top-level UI for the `formsAndFormats` Manager mode (Documents Editor).
// Primary selector switches between Documents, Proposals, Sign templates,
// and Asset library.

import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { FIREFLY_TARGET_ATTR } from "@/lib/firefly/fireflyTargetConvention";
import { canAdminDocuments } from "@/lib/document/documentRoles";
import { consumePendingFormsFormatsSubMode } from "@/lib/workspaceModeNavigation";
import { DocumentAssetLibraryPanel } from "@/components/document-builder/assets/DocumentAssetLibraryPanel";
import { DocumentsModePanel } from "@/components/document-builder/DocumentsModePanel";
import { SectionTabSlider } from "./SectionTabSlider";
import { ProposalFormatsModePanel } from "./ProposalFormatsModePanel";
import { SignTemplatesPanel } from "@/components/sign/SignTemplatesPanel";
import {
  FORMS_AND_FORMATS_SUB_MODE_OPTIONS,
  FORMS_FORMATS_SUB_MODE_BLURB,
  type FormsAndFormatsSubMode,
  normalizeFormsFormatsSubMode,
} from "./formsAndFormatsSubModes";

export type { FormsAndFormatsSubMode } from "./formsAndFormatsSubModes";
export { FORMS_AND_FORMATS_SUB_MODE_OPTIONS } from "./formsAndFormatsSubModes";

export function FormsAndFormatsModePanel() {
  const auth = useAuth();
  const canAdmin = canAdminDocuments(auth);
  const [subMode, setSubMode] = useState<FormsAndFormatsSubMode>(() => {
    return normalizeFormsFormatsSubMode(consumePendingFormsFormatsSubMode()) ?? "documents";
  });

  useEffect(() => {
    const pending = normalizeFormsFormatsSubMode(consumePendingFormsFormatsSubMode());
    if (pending) setSubMode(pending);
  }, []);

  return (
    <div {...{ [FIREFLY_TARGET_ATTR]: "ff-surface" }} className="space-y-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-zinc-100">
            Documents Editor
          </h2>
          <p className="text-sm text-gray-600 dark:text-zinc-300">{FORMS_FORMATS_SUB_MODE_BLURB[subMode]}</p>
        </div>
        <div className="shrink-0 self-center">
          <SectionTabSlider
            ariaLabel="Documents editor mode"
            fireflyTargetId="ff-sub-mode-tabs"
            tabTargetIds={{
              documents: "ff-documents-tab",
              proposals: "ff-proposals-tab",
              signTemplates: "ff-sign-templates-tab",
              assetLibrary: "ff-asset-library-tab",
            }}
            options={FORMS_AND_FORMATS_SUB_MODE_OPTIONS}
            value={subMode}
            onChange={setSubMode}
          />
        </div>
      </header>

      {subMode === "proposals" ? (
        <ProposalFormatsModePanel />
      ) : subMode === "signTemplates" ? (
        <SignTemplatesPanel />
      ) : subMode === "assetLibrary" ? (
        <DocumentAssetLibraryPanel canUpload={canAdmin} />
      ) : (
        <DocumentsModePanel />
      )}
    </div>
  );
}
